import { getCookie } from '@/lib/config/cookies';
import { logger } from '@/lib/telemetry/logger';
import type { AnalyticsProvider, ExperimentResult } from '../types';

const REDDIT_PIXEL_ID = 'a2_ivvodglvv8bo';

interface RedditInitOptions {
  externalId?: string;
  email?: string;
  phoneNumber?: string;
}

interface RedditSignUpMetadata {
  conversionId: string;
  externalId: string;
}

type RedditCommand =
  | [command: 'init', pixelId: string, options?: RedditInitOptions]
  | [command: 'track', event: 'PageVisit']
  | [command: 'track', event: 'SignUp', metadata: RedditSignUpMetadata]
  | [command: 'disableFirstPartyCookies']
  | [command: 'enableFirstPartyCookies'];

interface RedditTracker {
  (...args: RedditCommand): void;
  callQueue: RedditCommand[];
  sendEvent?: (...args: RedditCommand) => void;
}

export interface RedditMatchUser {
  id: string;
  email: string;
  phoneNumber: string | null;
}

/** What the pixel currently holds for this page: whose keys, and whether stored keys were checked. */
export interface RedditIdentity {
  readonly userId: string | null;
  readonly matchKeys: string | null;
  readonly checkedStoredKeys: boolean;
}

export const INITIAL_REDDIT_IDENTITY: RedditIdentity = {
  userId: null,
  matchKeys: null,
  checkedStoredKeys: false,
};

type TrackedProperties = Parameters<AnalyticsProvider['trackEvent']>[1];

declare global {
  interface Window {
    rdt?: RedditTracker;
  }
}

// One page load, one queue. pixel.js attaches whatever match keys it holds to
// an event, so a signup waits here until RedditPixel has brought those keys in
// line with the account that signed up.
let pendingSignUp: RedditSignUpMetadata | null = null;
let identifiedUserId: string | null = null;

export function shouldLoadRedditPixel(state: {
  environment: string | undefined;
  isImpersonating: boolean;
}): boolean {
  return state.environment === 'production' && !state.isImpersonating;
}

/**
 * Brings the pixel's match keys in line with the signed-in user, or with none,
 * sends a signup that was waiting for that user, and returns what the pixel
 * holds afterwards.
 */
export function syncRedditIdentity(
  current: RedditIdentity,
  user: RedditMatchUser | null,
): RedditIdentity {
  const next = user === null ? forgetSignedOutUser(current) : identifySignedInUser(current, user);

  if (pendingSignUp !== null && pendingSignUp.externalId === identifiedUserId) {
    const signUp = pendingSignUp;
    pendingSignUp = null;
    sendSignUp(signUp);
  }

  return next;
}

/**
 * Sends a PageVisit for a new path and returns the path recorded as visited,
 * or null after a failure so the next visit to any path, the previous one
 * included, is sent.
 */
export function trackRedditPageVisit(lastPath: string | null, pathname: string): string | null {
  if (lastPath === pathname) return lastPath;

  try {
    initializeRedditPixel();
    window.rdt?.('track', 'PageVisit');

    return pathname;
  } catch {
    logger.warn('Reddit page visit failed', { eventName: 'PageVisit' });

    return null;
  }
}

export function createRedditProvider(): AnalyticsProvider {
  return new RedditAnalyticsProvider();
}

// A write-only sink: flags and experiments stay with the primary provider.
class RedditAnalyticsProvider implements AnalyticsProvider {
  private accountId: string | null = null;

  async initialize(): Promise<void> {}

  checkGate(): boolean {
    return false;
  }

  getConfig(): ReturnType<AnalyticsProvider['getConfig']> {
    return {};
  }

  getExperiment(): ExperimentResult {
    return { value: null, ruleID: '' };
  }

  identifyUser(userId: string): void {
    this.accountId = userId;
  }

  shutdown(): void {
    this.accountId = null;
  }

  trackEvent(name: string, properties: TrackedProperties): void {
    if (typeof window === 'undefined') return;
    if (name !== 'auth_method_used' || properties.isNewUser !== true) return;

    if (this.accountId === null || this.accountId === '') {
      logger.warn('Reddit signup conversion skipped: missing account identity', {
        eventName: name,
      });

      return;
    }

    // The auth handler emits this before RedditPixel has synced the account's
    // match keys (and, after an OAuth redirect, before it mounts), so the
    // signup waits for that sync unless it already happened. Only a pixel.js
    // that RedditPixel loaded sends it. A server-side SignUp with the same
    // conversion ID is deduplicated with it.
    initializeRedditPixel();
    const signUp: RedditSignUpMetadata = {
      conversionId: `signup:${this.accountId}`,
      externalId: this.accountId,
    };

    if (identifiedUserId === this.accountId) {
      sendSignUp(signUp);
    } else {
      pendingSignUp = signUp;
    }
  }
}

function sendSignUp(signUp: RedditSignUpMetadata): void {
  try {
    window.rdt?.('track', 'SignUp', signUp);
  } catch {
    logger.warn('Reddit signup conversion failed', { eventName: 'SignUp' });
  }
}

function identifySignedInUser(current: RedditIdentity, user: RedditMatchUser): RedditIdentity {
  const matchKeys = `${user.id}|${user.email}|${user.phoneNumber ?? ''}`;
  if (current.matchKeys === matchKeys) return current;

  // Everything kept for a different user identified earlier on this page,
  // and any stored key this user lacks, belongs to someone else or was removed.
  const stored = storedRedditMatchKeys();
  const isOtherUser = current.userId !== null && current.userId !== user.id;
  const hasForeignKeys =
    isOtherUser ||
    (user.email === '' && stored.email) ||
    (user.phoneNumber === null && stored.phone);

  // Only keys that reached the pixel let a waiting signup go out.
  runIdentityUpdate(() => {
    initializeRedditPixel();
    if (hasForeignKeys) forgetRedditUser();
    identifyRedditUser(user);
    identifiedUserId = user.id;
  });

  return { userId: user.id, matchKeys, checkedStoredKeys: true };
}

function forgetSignedOutUser(current: RedditIdentity): RedditIdentity {
  // pixel.js deletes the cookies only once it loads; checking them again
  // before then would queue the same cleanup twice.
  const stored = current.checkedStoredKeys ? null : storedRedditMatchKeys();
  const hasKeys = current.matchKeys !== null || stored?.email === true || stored?.phone === true;

  // Signed out, here or before this page loaded: a shared device must not
  // keep the previous user's keys, which pixel.js reads back from cookies.
  if (hasKeys) {
    runIdentityUpdate(() => {
      initializeRedditPixel();
      forgetRedditUser();
    });
  }

  identifiedUserId = null;

  return { userId: null, matchKeys: null, checkedStoredKeys: true };
}

// An error thrown from vendor code inside a React effect reaches the root
// error boundary, so identity updates are logged instead.
function runIdentityUpdate(update: () => void): void {
  try {
    update();
  } catch {
    logger.warn('Reddit identity update failed', { vendor: 'reddit' });
  }
}

/**
 * Creates `window.rdt` as the vendor command queue and queues `init`, once per
 * page. pixel.js drains `callQueue` on load and then routes calls through
 * `sendEvent`; only `RedditPixel` loads pixel.js, so a queue created elsewhere
 * waits for it. A new queue also starts the page's signup state afresh: no
 * account identified and no signup waiting.
 */
export function initializeRedditPixel(): void {
  if (typeof window === 'undefined' || window.rdt !== undefined) return;

  const tracker: RedditTracker = Object.assign(
    (...args: RedditCommand): void => {
      if (tracker.sendEvent !== undefined) {
        tracker.sendEvent.apply(tracker, args);
      } else {
        tracker.callQueue = [...tracker.callQueue, args];
      }
    },
    { callQueue: [] },
  );

  // A new queue means a new page: nothing is identified or waiting yet.
  pendingSignUp = null;
  identifiedUserId = null;
  window.rdt = tracker;
  tracker('init', REDDIT_PIXEL_ID);
}

/**
 * Re-runs init with the signed-in user's match keys; pixel.js normalizes and
 * SHA-256 hashes them before anything leaves the browser.
 */
function identifyRedditUser(user: RedditMatchUser): void {
  if (typeof window === 'undefined' || window.rdt === undefined) return;

  const options: RedditInitOptions = { externalId: user.id };
  if (user.email !== '') options.email = user.email;
  if (user.phoneNumber !== null) options.phoneNumber = user.phoneNumber;

  window.rdt('init', REDDIT_PIXEL_ID, options);
}

/**
 * pixel.js deletes all of its cookies (hashed email and phone, click ID,
 * browser ID) on `disableFirstPartyCookies`, but keeps match keys an earlier
 * `init` passed in, and `enableFirstPartyCookies` writes them straight back.
 * A blank `init` while cookies are off rebuilds its config without them, so it
 * has to come between the two.
 */
function forgetRedditUser(): void {
  if (typeof window === 'undefined' || window.rdt === undefined) return;

  window.rdt('disableFirstPartyCookies');
  window.rdt('init', REDDIT_PIXEL_ID);
  window.rdt('enableFirstPartyCookies');
}

/** Which hashed match keys pixel.js keeps in its cookies between page loads. */
function storedRedditMatchKeys(): { email: boolean; phone: boolean } {
  return { email: getCookie('_rdt_em') !== undefined, phone: getCookie('_rdt_pn') !== undefined };
}
